<template>
  <sfs-base-list-filter
    title="Filter WO Incentive Report"
    :table-name="tableName"
    :applied-filters="appliedFilters"
    :displayed-columns="displayedColumns"
    :available-columns="availableColumns"
    :column-picker="false"
    @filters-applied="onFiltersApplied"
    @filters-reset="onDefaultPresetApplied"
    @filter-cleared="onFilterCleared"
    :advanced-filter="false"
    :popover-filter="true"
  >
    <template #quick-filters>
      <b-form-group
        label="Checked Out"
        label-for="quick-filter-checked_out"
        label-sr-only
      >
        <div class="d-flex">
          <sfs-date-range-picker
            id="quick-filter-checked_out"
            :value="checkedOutDates"
            placeholder="Checked Out Between"
            @on-change="onDatesChange"
          />
          <b-input-group-append>
            <b-button
              id="quick-filter-apply-button"
              @click="applyFilters"
              variant="primary"
            >
              <i class="fa fa-search" />
            </b-button>
          </b-input-group-append>
        </div>
      </b-form-group>
      <div class="font-size-sm mt-1 mb-2">
        <b-link @click="setCheckedOutRange(lastWeek)">
          Last week
        </b-link>
        <span class="pl-1 pr-1">|</span>
        <b-link @click="setCheckedOutRange(twoWeeksAgo)">
          2 weeks ago
        </b-link>
        <span class="pl-1 pr-1">|</span>
        <b-link @click="setCheckedOutRange(currentWeek)">
          Current week
        </b-link>
        <span class="pl-1 pr-1">|</span>
        <b-link @click="setCheckedOutRange(currentMonth)">
          Current month
        </b-link>
      </div>
    </template>

    <template #popover-filters>
      <b-row>
        <b-col cols="12">
          <b-form-group
            label="Clients"
            label-for="popover-filter-client_ids"
            label-cols-lg="5"
            content-cols-lg="7"
          >
            <client-select
              id="popover-filter-client_ids"
              placeholder="All Clients"
              multiple
              :value="clients || []"
              @input="onClientChange"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group
            label="Technicians"
            label-for="popover-filter-user_ids"
            label-cols-lg="5"
            content-cols-lg="7"
          >
            <user-select
              id="popover-filter-user_ids"
              placeholder="All Technicians"
              multiple
              :value="users || []"
              :filter-params="technicianSelectParams"
              @input="onUsersChange"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </template>

    <template #right-tray>
      <sfs-download-button
        v-b-tooltip.hover
        :provider-params="filters"
        filename="Work Order Incentive.csv"
        :file-provider="$analyticsAPI.getWorkOrderIncentiveReportSpreadsheet"
        title="Download spreadsheet"
      />
    </template>
  </sfs-base-list-filter>
</template>

<script>
import { DateTime } from 'luxon';
import ClientSelect from '@/components/admin/clients/Select.vue';
import UserSelect from '@/components/admin/users/Select.vue';
import listFilterMixin from '@/mixins/listFilterMixin';
import { USER_TECHNICIAN_CATEGORY } from '@/constants/users';
import {
  WORK_ORDER_INCENTIVE_REPORT_TABLE_NAME,
  WORK_ORDER_INCENTIVE_REPORT_TABLE_DEFAULT_COLUMNS,
  WORK_ORDER_INCENTIVE_REPORT_TABLE_COLUMN_OPTIONS,
} from '@/constants/reports';

// Flatpickr expects "Aug 4, 2026". Always use English so locale never breaks parsing.
const FILTER_DATE_FORMAT = 'LLL d, yyyy';
const FILTER_DATE_LOCALE = { locale: 'en' };

function weekRange(weeksAgo = 0) {
  // Sun–Sat week, matching Admin Dashboard presets
  const weekStart = DateTime.now().startOf('week').minus({ week: weeksAgo });
  return [
    weekStart.minus({ day: 1 }).toISODate(),
    weekStart.plus({ day: 5 }).toISODate(),
  ];
}

export default {
  name: 'WorkOrderIncentiveListFilter',
  mixins: [listFilterMixin],
  components: {
    ClientSelect,
    UserSelect,
  },
  data() {
    const [lastWeekStart, lastWeekEnd] = weekRange(1);
    const [twoWeeksAgoStart, twoWeeksAgoEnd] = weekRange(2);

    return {
      tableName: WORK_ORDER_INCENTIVE_REPORT_TABLE_NAME,
      availableColumns: WORK_ORDER_INCENTIVE_REPORT_TABLE_COLUMN_OPTIONS,
      technicianSelectParams: { category: USER_TECHNICIAN_CATEGORY, active: true },
      lastWeek: [lastWeekStart, lastWeekEnd],
      twoWeeksAgo: [twoWeeksAgoStart, twoWeeksAgoEnd],
      currentWeek: weekRange(0),
      currentMonth: [
        DateTime.now().startOf('month').toISODate(),
        DateTime.now().endOf('month').toISODate(),
      ],
    };
  },
  mounted() {
    this.loadInitialFilters();
  },
  computed: {
    // Date picker needs JS Dates, not filter strings
    checkedOutDates() {
      return [
        this.parseFilterDate(this.filters.checked_out_from),
        this.parseFilterDate(this.filters.checked_out_to),
      ];
    },
  },
  methods: {
    formatFilterDate(date) {
      return DateTime.fromJSDate(date).setLocale('en').toFormat(FILTER_DATE_FORMAT);
    },
    formatIsoDate(isoDate) {
      return DateTime.fromISO(isoDate).setLocale('en').toFormat(FILTER_DATE_FORMAT);
    },
    parseFilterDate(value) {
      if (!value) {
        return null;
      }
      const parsed = DateTime.fromFormat(String(value), FILTER_DATE_FORMAT, FILTER_DATE_LOCALE);
      return parsed.isValid ? parsed.toJSDate() : null;
    },
    setCheckedOutRange([fromIso, toIso]) {
      this.$set(this.filters, 'checked_out_from', this.formatIsoDate(fromIso));
      this.$set(this.filters, 'checked_out_to', this.formatIsoDate(toIso));
      this.applyFilters();
    },
    onDatesChange(selectedDates) {
      if (!selectedDates[0] || !selectedDates[1]) {
        return;
      }
      this.$set(this.filters, 'checked_out_from', this.formatFilterDate(selectedDates[0]));
      this.$set(this.filters, 'checked_out_to', this.formatFilterDate(selectedDates[1]));
      this.applyFilters();
    },
    onClientChange(selected) {
      this.setMultiIdFilter('client_ids', 'clients', selected);
    },
    onUsersChange(selected) {
      this.setMultiIdFilter('user_ids', 'users', selected);
    },
    setMultiIdFilter(filterKey, stateKey, selected) {
      const list = Array.isArray(selected) ? selected.filter(Boolean) : [];
      if (list.length) {
        this[stateKey] = list;
        this.$set(this.filters, filterKey, list.map((item) => String(item.id)));
      } else {
        this[stateKey] = null;
        this.$delete(this.filters, filterKey);
      }
    },
    onDefaultPresetApplied() {
      this.clients = null;
      this.users = null;
      this.displayedColumns = WORK_ORDER_INCENTIVE_REPORT_TABLE_DEFAULT_COLUMNS;
      this.filters = {
        checked_out_from: this.formatIsoDate(this.lastWeek[0]),
        checked_out_to: this.formatIsoDate(this.lastWeek[1]),
      };
      this.applyFilters();
    },
    loadInitialFilters() {
      const clientIds = this.initialFilters.client_ids || [];
      const userIds = this.initialFilters.user_ids || [];

      Promise.all([
        Promise.all(clientIds.map((id) => this.$clientsAPI.get(id))),
        Promise.all(userIds.map((id) => this.$usersAPI.get(id))),
      ]).then(([clients, users]) => {
        this.clients = clients.length ? clients : null;
        this.users = users.length ? users : null;
        this.applyFilters();
      });
    },
  },
};
</script>
